// Prosjektside (ny) — redesign of the order detail page.
//
// Same data as the old OrderDetail.js had (action `order_detail`), presented in
// the card layout from "Ny prosjektside for kunde og kundebehandler": a hero row
// (levering / støpevær / bestilling), map, delivery plan, then info cards.
// This is the ONLY order detail page, served at the classic URL
// /bestillinger/order-details/:orderId (the classic page it replaced is
// removed). The edit page at /bestillinger/edit-details/:orderId still exists.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FactoryIcon from "@mui/icons-material/Factory";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RouteIcon from "@mui/icons-material/Route";
import EmailIcon from "@mui/icons-material/Email";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DownloadIcon from "@mui/icons-material/Download";
import PhoneIcon from "@mui/icons-material/Phone";
import PaymentsIcon from "@mui/icons-material/Payments";
import LinkIcon from "@mui/icons-material/Link";
import CloudIcon from "@mui/icons-material/Cloud";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import OrderEventTimeline from "../OrderDetails/OrderEventTimeline";
import VendorRequestPanel from "components/VendorRequestPanel";
import StatusChangeDialog from "components/StatusChangeDialog";
import OrderInfoDialog from "components/OrderInfoDialog";
import ManualEmailDialog from "components/ManualEmailDialog";
import StatusInfoDialog from "components/StatusInfoDialog";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { notifyCenter } from "utils/toast";
import {
  getOrderById,
  fetchStatusList,
  changeOrderStatus,
  getOrderPriceAnalysis,
  getVendorLink,
  saveVendorNote,
  fetchVendorList,
  changeVendor,
  updateOrderTotal,
} from "api/apiService";

/* ------------------------------- design tokens ----------------------------- */
const BRAND = "#006bb7";
const INK = "#111c2d";
const MUTED = "#67748e";
const LINE = "#e6ecf3";
const PAGE_BG = "#f4f7fb";

const CARD = {
  borderRadius: "16px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 2px 14px rgba(16,24,40,.05)",
  height: "100%",
};

// Fremdriften i leveringskortet, keyed on the status VALUE from the `status`
// table — never on its Norwegian label. Labels are what customers read, and
// they get rewritten; a renamed label used to drop every order back to the
// first step without a word.
//
// Steget en status peker på er det siste ordren har NÅDD. Det vises som
// gjeldende (blått); stegene før er fullført (grønne). «Betalt» er altså
// gjeldende mens ordren står i «Behandler» — slik skissen viser det.
const STEPS = [
  { label: "Opprettet", Icon: ReceiptLongIcon },
  { label: "Kvalitetssikret", Icon: CheckIcon },
  { label: "Betalt", Icon: CreditCardIcon },
  { label: "Produsent", Icon: FactoryIcon },
  { label: "Levert", Icon: LocalShippingIcon },
];
const STEP_BY_STATUS = {
  quoting: 0,
  "checkout-draft": 0,
  "manual-review": 0,
  "awaiting-quote": 0,
  pending: 1,
  "awaiting-payment": 1,
  "payment-failed": 1,
  failed: 1,
  "on-hold": 1,
  processing: 2,
  "awaiting-vendor": 2,
  "vendor-rejected": 2,
  "vendor-confirmed": 3,
  scheduled: 3,
  waitingdelivery: 3,
  "in-delivery": 3,
  delivered: 4,
  completed: 4,
};
// Ferdige ordre: alle stegene er fullført, også det siste.
const ALL_DONE = new Set(["delivered", "completed"]);

const MONTHS = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

/** «05.10.2026» eller «2026-10-05» → «5. Oktober 2026». Annen tekst vises som den er. */
function longDate(value) {
  const v = String(value || "").trim();
  let m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${Number(m[1])}. ${MONTHS[Number(m[2]) - 1] || m[2]} ${m[3]}`;
  m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[3])}. ${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}`;
  return v;
}

/** «Kl. 08:00 - 10:00» → «08.00 - 10.00», som i skissen. */
const clockText = (value) =>
  String(value || "")
    .replace(/^\s*kl\.?\s*/i, "")
    .replace(/(\d{1,2}):(\d{2})/g, "$1.$2")
    .trim();

const isBlank = (v) => v == null || String(v).trim() === "";
const show = (v, fallback = "—") => (isBlank(v) ? fallback : String(v));

/** Coordinates are meaningless when geocoding failed (the backend returns 1,1). */
function hasRealCoords(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (a === 0 && b === 0) return false;
  if (a === 1 && b === 1) return false;
  return true;
}

/* ------------------------------ small building blocks ---------------------- */
function CardHead({ icon, title, tone = BRAND, action }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      <Box sx={{ color: tone, display: "grid", placeItems: "center" }}>{icon}</Box>
      <SoftTypography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: tone,
          flexGrow: 1,
        }}
      >
        {title}
      </SoftTypography>
      {action}
    </Stack>
  );
}
CardHead.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  tone: PropTypes.string,
  action: PropTypes.node,
};

/** small label above a bold value — the repeating unit of the reference design */
function Stat({ label, value, light = false, icon }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <SoftTypography
          sx={{
            fontSize: 10.5,
            letterSpacing: ".05em",
            textTransform: "uppercase",
            color: light ? "rgba(255,255,255,.78)" : MUTED,
          }}
        >
          {label}
        </SoftTypography>
      </Stack>
      <SoftTypography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: light ? "#fff" : INK,
          wordBreak: "break-word",
          lineHeight: 1.35,
        }}
      >
        {value}
      </SoftTypography>
    </Box>
  );
}
Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  light: PropTypes.bool,
  icon: PropTypes.node,
};

/** icon + text row used inside the four bottom info cards */
function InfoRow({ icon, children }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.25} mb={1.25}>
      <Box sx={{ color: MUTED, mt: 0.2, display: "grid", placeItems: "center" }}>{icon}</Box>
      <SoftTypography sx={{ fontSize: 13.5, color: INK, wordBreak: "break-word", flexGrow: 1 }}>
        {children}
      </SoftTypography>
    </Stack>
  );
}
InfoRow.propTypes = { icon: PropTypes.node, children: PropTypes.node };

const STEP_GREEN = "#22c55e";

/** Opprettet → Kvalitetssikret → Betalt → Produsent → Levert */
function DeliverySteps({ current, allDone }) {
  const size = { xs: 40, sm: 54 };
  return (
    <Box sx={{ mt: { xs: 3, sm: 4 }, display: "flex" }}>
      {STEPS.map(({ label, Icon }, i) => {
        const done = allDone || i < current;
        const active = !allDone && i === current;
        // Linja mot neste steg: grønn mellom fullførte, grønn som går over i
        // hvitt inn mot det gjeldende, ellers dempet.
        const line =
          allDone || i + 1 < current
            ? STEP_GREEN
            : i + 1 === current
            ? `linear-gradient(90deg, ${STEP_GREEN} 55%, #fff 55%)`
            : "rgba(255,255,255,.35)";
        return (
          <Box key={label} sx={{ flex: 1, minWidth: 0, position: "relative", textAlign: "center" }}>
            {i < STEPS.length - 1 && (
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: 19, sm: 26 },
                  left: "50%",
                  width: "100%",
                  height: 3,
                  borderRadius: 2,
                  background: line,
                }}
              />
            )}
            <Box
              aria-current={active ? "step" : undefined}
              sx={{
                position: "relative",
                mx: "auto",
                width: size,
                height: size,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                backgroundColor: done ? STEP_GREEN : active ? "#0b63c5" : "#c9dcf5",
                border: done
                  ? "3px solid rgba(255,255,255,.55)"
                  : active
                  ? "4px solid #fff"
                  : "3px solid rgba(255,255,255,.35)",
                boxShadow: done
                  ? "0 0 0 4px rgba(34,197,94,.25)"
                  : active
                  ? "0 0 0 5px rgba(255,255,255,.2)"
                  : "none",
              }}
            >
              <Icon sx={{ fontSize: { xs: 20, sm: 26 }, color: done || active ? "#fff" : "#2f6fb8" }} />
            </Box>
            <SoftTypography
              sx={{
                mt: 1,
                fontSize: { xs: 10.5, sm: 14 },
                fontWeight: 700,
                color: done || active ? "#fff" : "rgba(255,255,255,.85)",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </SoftTypography>
          </Box>
        );
      })}
    </Box>
  );
}
DeliverySteps.propTypes = { current: PropTypes.number, allDone: PropTypes.bool };

/* ==================================== page ================================= */
function OrderDetailsV2() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = role === "admin" || role === "admin_staff";


  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Melding til produsenten, lagret på ordren og vist på leveranselenken.
  const [vendorNote, setVendorNote] = useState("");
  const [savingVendorNote, setSavingVendorNote] = useState(false);

  // Admin-redigering av totalpris (inkl. mva.) — samme update_new_total som
  // den gamle ordresiden brukte: backend regner selv ut eks. mva. per lass.
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceValue, setPriceValue] = useState("");
  const [priceSaving, setPriceSaving] = useState(false);

  const handleSavePrice = async () => {
    // Godta både «20 285,50» og «20285.50».
    const num = Number(String(priceValue).replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(num) || num <= 0) {
      notifyCenter("error", "Oppgi et gyldig beløp (inkl. mva.).", { duration: 3000 });
      return;
    }
    setPriceSaving(true);
    try {
      const res = await updateOrderTotal(orderId, num);
      if (res?.ok) {
        notifyCenter("success", "Totalprisen er oppdatert.", { duration: 2500 });
        setPriceOpen(false);
        load({ silent: true });
      } else {
        notifyCenter("error", `Kunne ikke oppdatere prisen: ${res?.error || res?.message || "ukjent feil"}`, { duration: 4000 });
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke oppdatere prisen.", { duration: 4000 });
    } finally {
      setPriceSaving(false);
    }
  };

  // Forhåndsutfyll med det som allerede er lagret.
  useEffect(() => {
    setVendorNote(data?.vendor_note || "");
  }, [data?.vendor_note]);

  const handleSaveVendorNote = async () => {
    setSavingVendorNote(true);
    try {
      const res = await saveVendorNote(orderId, vendorNote);
      notifyCenter(
        res?.ok ? "success" : "error",
        res?.ok ? "Notatet er lagret." : "Kunne ikke lagre notatet."
      );
    } catch (e) {
      notifyCenter("error", "Kunne ikke lagre notatet.");
    } finally {
      setSavingVendorNote(false);
    }
  };

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [nextStatus, setNextStatus] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  // `silent`: bakgrunnsoppdatering når hendelsesloggen ser en ny hendelse.
  // Uten den byttes hele siden ut med lastehjulet hver gang, og en feil i en
  // bakgrunnssjekk ville erstattet ordren med en feilmelding.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await getOrderById({ order_id: orderId });
      if (res?.status && res?.data) setData(res.data);
      else if (!silent) setError(res?.message || "Kunne ikke hente ordren.");
    } catch (e) {
      if (!silent) setError(e?.message || "Nettverksfeil.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  /* -------------------- kundeinfo og leverandørbytte ----------------------- */
  const [infoOpen, setInfoOpen] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [changingVendor, setChangingVendor] = useState(false);

  const loadVendors = useCallback(async () => {
    if (vendorOptions.length) return;
    try {
      const res = await fetchVendorList();
      if (res?.status) setVendorOptions(res.vendor_list || []);
    } catch (e) {
      notifyCenter("error", "Kunne ikke hente leverandørlisten.");
    }
  }, [vendorOptions.length]);

  const applyVendor = async (vendorId) => {
    if (!vendorId) return;
    setChangingVendor(true);
    try {
      const res = await changeVendor(orderId, vendorId, data?.address);
      if (res?.status) {
        notifyCenter("success", "Leverandør byttet.");
        await load();
      } else {
        notifyCenter("error", res?.message || "Kunne ikke bytte leverandør.");
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke bytte leverandør.");
    } finally {
      setChangingVendor(false);
    }
  };

  /* --------------------------------- derived -------------------------------- */
  const statusText = data?.order_status?.status_text || "";
  const statusKey = data?.order_status_key || "";
  const stepIndex = useMemo(() => {
    const key = String(statusKey).trim().toLowerCase();
    return STEP_BY_STATUS[key] ?? 0;
  }, [statusKey]);

  const deliveries = useMemo(
    () => (Array.isArray(data?.deliveries) ? data.deliveries : []),
    [data]
  );
  const firstDelivery = deliveries[0] || {};
  const deliveryWhen = data?.delivery_date?.[0] || {};
  // Avtalt tid (fra lassene, etter at produsenten har bekreftet) går foran
  // kundens ønske. «14.09.2026 | Kl. 08:00» fra orderDetail.js.
  const [confirmedDate, confirmedTime] = data?.confirmed_delivery
    ? String(data.confirmed_delivery).split(" | ")
    : [];
  const TIMING_TEXT = { asap: "Så snart som mulig", later: "Avtales senere" };

  const payUrl = useMemo(() => {
    const raw = data?.order_meta?._pay_url;
    const url = Array.isArray(raw) ? raw[0] : raw;
    return url && String(url).startsWith("http") ? String(url) : null;
  }, [data]);

  // Producer acceptance link. The token is `_vendor_hash`, minted on demand and
  // deliberately separate from the customer's `_custom_hash` — so it is fetched
  // from the admin-only get_vendor_link action rather than read out of
  // order_meta, which may not carry it yet.
  const copyVendorLink = async () => {
    try {
      const res = await getVendorLink(orderId);
      if (!res?.ok || !res.token) {
        notifyCenter("error", res?.message || "Kunne ikke hente leveranselenken.");
        return;
      }
      await navigator.clipboard.writeText(`${window.location.origin}/leveranse/${res.token}`);
      notifyCenter("success", "Leveranselenke kopiert.");
    } catch (e) {
      notifyCenter("error", "Kunne ikke kopiere lenken.");
    }
  };

  const coordsOk = hasRealCoords(data?.lat, data?.lng);
  const mapsHref = data?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address)}`
    : null;

  const distanceLabel = useMemo(() => {
    const km = Number(data?.distance);
    if (!Number.isFinite(km)) return null;
    // The backend substitutes 1 000 000 when geocoding fails — never present
    // that as a real distance.
    if (km >= 999999) return null;
    return `${km.toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
  }, [data]);

  const additives = useCallback((d) => {
    const list = [
      [d.decline, "Støp med fall"],
      [d.retardation, "Forsinkelse"],
      [d.steel_fiber, "Stålfiber"],
      [d.accelerator, "Akselerator"],
      [d.pump_wash, "Pumpevask"],
      [d.extra_volume, "Ekstra volum"],
      [d.add_hose, d.hose_length ? `Slange ${d.hose_length} m` : "Slange"],
    ]
      .filter(([on]) => !isBlank(on))
      .map(([, label]) => label);
    return list.length ? list.join(", ") : "Ingen";
  }, []);

  /* --------------------------------- actions -------------------------------- */
  const openStatusDialog = () => setStatusOpen(true);

  const saveStatus = async () => {
    if (!nextStatus) return;
    setSavingStatus(true);
    try {
      const res = await changeOrderStatus(orderId, nextStatus);
      // change_order_status signals success with `ok`, not `status`
      if (res?.ok !== true) {
        notifyCenter("error", res?.message || "Statusendring feilet.");
      } else {
        notifyCenter("success", "Status oppdatert.");
        setStatusOpen(false);
        setNextStatus("");
        load();
      }
    } catch (e) {
      notifyCenter("error", "Statusendring feilet.");
    } finally {
      setSavingStatus(false);
    }
  };

  const downloadAnalysis = async () => {
    try {
      const res = await getOrderPriceAnalysis({ order_id: orderId });
      if (!res || res.status === false) {
        notifyCenter("error", res?.message || "Prisanalyse ikke tilgjengelig.");
        return;
      }
      const blob = new Blob([JSON.stringify(res.data ?? res, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prisanalyse_ordre_${orderId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      notifyCenter("success", "Prisanalyse lastet ned.");
    } catch (e) {
      notifyCenter("error", "Kunne ikke hente prisanalyse.");
    }
  };

  /* --------------------------------- states --------------------------------- */
  if (loading) {
    return (
      <DashboardLayout>
        <SoftBox sx={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <SoftBox p={3}>
          <Card sx={{ ...CARD, height: "auto" }}>
            <SoftBox p={3}>
              <SoftTypography sx={{ fontSize: 16, fontWeight: 700, color: INK }}>
                Kunne ikke vise ordren
              </SoftTypography>
              <SoftTypography sx={{ fontSize: 14, color: MUTED, mt: 1 }}>
                {error || "Ukjent feil."}
              </SoftTypography>
              <SoftButton sx={{ mt: 2 }} onClick={load}>
                Prøv igjen
              </SoftButton>
            </SoftBox>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  /* --------------------------------- render --------------------------------- */
  return (
    <DashboardLayout>
      <SoftBox sx={{ backgroundColor: PAGE_BG, mx: -3, px: 3, pt: 2, pb: 6, minHeight: "100vh" }}>
        {/* ------------------------------- header ------------------------------- */}
        <Card sx={{ ...CARD, mb: 3, height: "auto" }}>
          <SoftBox p={3}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={2}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PlaceIcon sx={{ color: BRAND, fontSize: 24 }} />
                  <SoftTypography sx={{ fontSize: 22, fontWeight: 800, color: INK }}>
                    Leveringsadresse
                  </SoftTypography>
                </Stack>
                <SoftTypography
                  sx={{
                    fontSize: 13.5,
                    color: MUTED,
                    textTransform: "uppercase",
                    letterSpacing: ".03em",
                    mt: 0.5,
                  }}
                >
                  {show(data.address)}
                </SoftTypography>
              </Box>

              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Ordre ID: #${show(data.order_number)}`}
                  sx={{ fontWeight: 700, backgroundColor: "#fff", border: `1px solid ${LINE}` }}
                />
                <Chip
                  icon={<EventIcon sx={{ fontSize: 16 }} />}
                  label={`Bestilt: ${show(data.created_date?.date)}`}
                  sx={{ fontWeight: 600, backgroundColor: "#fff", border: `1px solid ${LINE}` }}
                />
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16, color: "#fff !important" }} />}
                  label={`Status: ${show(statusText)}`}
                  onClick={isAdmin ? openStatusDialog : undefined}
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    backgroundColor: "#0f8a43",
                    cursor: isAdmin ? "pointer" : "default",
                    "&:hover": { backgroundColor: isAdmin ? "#0c7538" : "#0f8a43" },
                  }}
                />
              </Stack>
            </Stack>
          </SoftBox>
        </Card>

        {/* ------------------------------ hero row ------------------------------ */}
        <Grid container spacing={3} mb={3}>
          {/* levering */}
          <Grid item xs={12} md={6} xl={5}>
            <Card
              sx={{
                ...CARD,
                border: "none",
                background: "linear-gradient(135deg, #0b63c5, #1478e6)",
                color: "#fff",
              }}
            >
              <SoftBox p={{ xs: 2.5, sm: 3.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <SoftTypography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: ".06em",
                        color: "rgba(255,255,255,.85)",
                      }}
                    >
                      PLANLAGT LEVERINGSTID
                    </SoftTypography>
                    <SoftTypography
                      sx={{
                        fontSize: { xs: 28, sm: 36 },
                        fontWeight: 800,
                        color: "#fff",
                        mt: 1,
                        lineHeight: 1.1,
                      }}
                    >
                      {longDate(
                        confirmedDate ||
                          show(deliveryWhen.date, TIMING_TEXT[data?.delivery_timing] || "Dato avtales")
                      )}
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: "#fff", mt: 0.5 }}>
                      {clockText(
                        confirmedDate
                          ? confirmedTime
                          : deliveryWhen.time_window || (isBlank(deliveryWhen.time) ? "" : deliveryWhen.time)
                      )}
                    </SoftTypography>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: { xs: 1.75, sm: 3 },
                      py: { xs: 0.75, sm: 1.1 },
                      borderRadius: "12px",
                      backgroundColor: "rgba(255,255,255,.2)",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 15 },
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    {show(statusText)}
                  </Box>
                </Stack>

                {confirmedDate && data?.customer_wish_label && (
                  <SoftTypography
                    sx={{ fontSize: 12, color: "rgba(255,255,255,.8)", mt: 1, display: "block" }}
                  >
                    Kundens ønske: {data.customer_wish_label}
                  </SoftTypography>
                )}

                {/* Tiden produsenten bekreftet på leveranselenken, med notatet
                    deres til oss. Lange notater brytes over inntil tre linjer;
                    hele teksten ligger i title. */}
                {confirmedDate && (
                  <SoftTypography
                    title={data?.vendor_response_note || undefined}
                    sx={{
                      fontSize: 12,
                      color: "rgba(255,255,255,.8)",
                      mt: 0.5,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    Produsent bekrefter: {longDate(confirmedDate)}
                    {confirmedTime ? ` | Kl. ${clockText(confirmedTime)}` : ""}
                    {!isBlank(data?.vendor_response_note) &&
                      ` | Notat: «${String(data.vendor_response_note).trim()}»`}
                  </SoftTypography>
                )}

                {/* Kunden kan oppgi flere alternativer, og produsenten velger ett
                    av dem på leveranselenken. Uten denne linja så admin bare det
                    første og visste ikke hva som var i spill. */}
                {!confirmedDate && data?.delivery_date?.length > 1 && (
                  <SoftTypography
                    sx={{ fontSize: 12, color: "rgba(255,255,255,.8)", mt: 1, display: "block" }}
                  >
                    + {data.delivery_date.length - 1} alternativ
                    {data.delivery_date.length - 1 === 1 ? "" : "er"}:{" "}
                    {data.delivery_date
                      .slice(1)
                      .map((d) => `${d.date} ${d.time_window || `kl. ${d.time}`}`)
                      .join(" · ")}
                  </SoftTypography>
                )}

                <DeliverySteps
                  current={stepIndex}
                  allDone={ALL_DONE.has(String(statusKey).trim().toLowerCase())}
                />
              </SoftBox>
            </Card>
          </Grid>

          {/* støpevær */}
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={{ ...CARD, border: "none", backgroundColor: "#e8f2fd" }}>
              <SoftBox p={3}>
                <CardHead icon={<CloudIcon sx={{ fontSize: 18 }} />} title="Støpevær" tone="#0b6fb8" />
                <SoftTypography sx={{ fontSize: 34, fontWeight: 800, color: INK, lineHeight: 1.1 }}>
                  {show(data.weather_temprature)}
                </SoftTypography>
                <SoftTypography sx={{ fontSize: 13.5, color: MUTED, mt: 0.5 }}>
                  {show(data.weather_text, "Værdata utilgjengelig")}
                </SoftTypography>
                <Divider sx={{ my: 2 }} />
                <SoftTypography sx={{ fontSize: 12, color: MUTED }}>
                  Været hentes for leveringsdagen og oppdateres frem mot støp.
                </SoftTypography>
              </SoftBox>
            </Card>
          </Grid>

          {/* bestilling */}
          <Grid item xs={12} xl={4}>
            <Card
              sx={{
                ...CARD,
                border: "none",
                background: "linear-gradient(135deg, #0f8a43, #17a55b)",
                color: "#fff",
              }}
            >
              <SoftBox p={3}>
                <SoftTypography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: ".07em",
                    color: "rgba(255,255,255,.85)",
                  }}
                >
                  DIN BESTILLING
                </SoftTypography>

                <Stack direction="row" alignItems="baseline" spacing={1} mt={1}>
                  <SoftTypography
                    sx={{ fontSize: 38, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}
                  >
                    {show(data.total_volume, "0")}
                  </SoftTypography>
                  <SoftTypography sx={{ fontSize: 19, fontWeight: 700, color: "#fff" }}>
                    m³
                  </SoftTypography>
                </Stack>

                <SoftTypography
                  sx={{ fontSize: 15, fontWeight: 700, color: "#fff", textTransform: "uppercase" }}
                >
                  {show(data.create_name)}
                </SoftTypography>
                <SoftTypography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.9)", mt: 0.5 }}>
                  {show(firstDelivery.concrete_type)}
                </SoftTypography>
                <SoftTypography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.9)" }}>
                  {show(firstDelivery.vehicle_type)}
                </SoftTypography>

                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,.25)" }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Stat light label="Mengde" value={`${show(data.total_volume, "0")} m³`} />
                  </Grid>
                  <Grid item xs={6}>
                    <Stat light label="Leveranse" value={show(data.deliveries_count)} />
                  </Grid>
                  <Grid item xs={6}>
                    <Stat light label="Verdi (eks. mva.)" value={show(data.order_subtotal)} />
                  </Grid>
                  <Grid item xs={6}>
                    <SoftBox display="flex" alignItems="flex-start" gap={0.5}>
                      <Stat light label="Totalt (inkl. mva.)" value={show(data.order_total)} />
                      {isAdmin && (
                        <Tooltip title="Endre totalpris">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPriceValue("");
                              setPriceOpen(true);
                            }}
                            sx={{ color: "rgba(255,255,255,.85)", mt: 0.5 }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </SoftBox>
                  </Grid>
                  <Grid item xs={6}>
                    <Stat light label="Betaling" value={show(data.order_status?.status_pay_text)} />
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>

        {/* ------------------------------- actions ------------------------------ */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <SoftButton
              fullWidth
              onClick={() => navigate("/bestillinger")}
              sx={{ backgroundColor: "#1b2440", color: "#fff", borderRadius: "12px", py: 1.3 }}
            >
              <ArrowBackIcon sx={{ fontSize: 17, mr: 0.75 }} />
              Alle bestillinger
            </SoftButton>
          </Grid>
          {isAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <SoftButton
                  fullWidth
                  onClick={openStatusDialog}
                  sx={{ backgroundColor: "#0f8a43", color: "#fff", borderRadius: "12px", py: 1.3 }}
                >
                  <SwapHorizIcon sx={{ fontSize: 18, mr: 0.75 }} />
                  Endre status
                </SoftButton>
                <IconButton
                  size="small"
                  aria-label="Slik fungerer statusene"
                  title="Slik fungerer statusene"
                  onClick={() => setStatusInfoOpen(true)}
                >
                  <InfoOutlinedIcon sx={{ color: "#344767" }} />
                </IconButton>
              </Box>
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <SoftButton
                fullWidth
                onClick={() => setManualOpen(true)}
                sx={{ backgroundColor: BRAND, color: "#fff", borderRadius: "12px", py: 1.3 }}
              >
                <EmailIcon sx={{ fontSize: 17, mr: 0.75 }} />
                Send e-post manuelt
              </SoftButton>
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <Tooltip title="Hent prisanalyse for ordren">
                <span style={{ display: "block" }}>
                  <SoftButton
                    fullWidth
                    onClick={downloadAnalysis}
                    sx={{ backgroundColor: "#8f9bb3", color: "#fff", borderRadius: "12px", py: 1.3 }}
                  >
                    <DownloadIcon sx={{ fontSize: 17, mr: 0.75 }} />
                    Prisanalyse
                  </SoftButton>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {/* -------------------------------- kart -------------------------------- */}
        <Card sx={{ ...CARD, mb: 3, height: "auto" }}>
          <Grid container>
            <Grid item xs={12} md={4}>
              <SoftBox p={3}>
                <CardHead icon={<PlaceIcon sx={{ fontSize: 18 }} />} title="Leveringsadresse på kart" />
                <SoftTypography sx={{ fontSize: 14, fontWeight: 700, color: INK }}>
                  {show(data.address)}
                </SoftTypography>

                {mapsHref && (
                  <SoftButton
                    component="a"
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2, borderColor: BRAND, color: BRAND, borderRadius: "10px" }}
                  >
                    Åpne i Google Maps
                    <OpenInNewIcon sx={{ fontSize: 15, ml: 0.5 }} />
                  </SoftButton>
                )}

                <Divider sx={{ my: 2 }} />
                <Stat
                  label="Avstand fra fabrikk"
                  value={distanceLabel || "Ikke beregnet"}
                  icon={<RouteIcon sx={{ fontSize: 14, color: MUTED }} />}
                />
                {!distanceLabel && (
                  <SoftTypography sx={{ fontSize: 11.5, color: "#c0392b", mt: 0.5, display: "block" }}>
                    Adressen kunne ikke geokodes — avstand og leverandør er ikke pålitelige.
                  </SoftTypography>
                )}
              </SoftBox>
            </Grid>

            <Grid item xs={12} md={8}>
              {/* Positioned container: the iframe is absolutely filled to its
                  edges. A grid/flex parent with centering would shrink-wrap the
                  iframe to its intrinsic 300px width instead of stretching it. */}
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 240, md: 300 },
                  backgroundColor: "#eef2f7",
                  overflow: "hidden",
                  borderTopRightRadius: { md: "16px" },
                  borderBottomRightRadius: { md: "16px" },
                }}
              >
                {coordsOk ? (
                  // OpenStreetMap needs no API key and no billing, so the map keeps
                  // working regardless of the Google Maps project state. The
                  // "Åpne i Google Maps" link above still handles directions.
                  <Box
                    component="iframe"
                    title="Leveringskart"
                    scrolling="no"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                      Number(data.lng) - 0.012
                    },${Number(data.lat) - 0.006},${Number(data.lng) + 0.012},${
                      Number(data.lat) + 0.006
                    }&layer=mapnik&marker=${data.lat},${data.lng}`}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                      display: "block",
                    }}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{ position: "absolute", inset: 0, p: 2 }}
                  >
                    <PlaceIcon sx={{ fontSize: 30, color: MUTED }} />
                    <SoftTypography sx={{ fontSize: 13, color: MUTED, textAlign: "center" }}>
                      Kart utilgjengelig — ordren mangler gyldige koordinater.
                    </SoftTypography>
                  </Stack>
                )}
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* --------------------------- leveringsplan ---------------------------- */}
        <Card sx={{ ...CARD, mb: 3, height: "auto" }}>
          <SoftBox p={3}>
            <CardHead
              icon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
              title={`Leveringsplan – ${deliveries.length} lass`}
            />
            <Grid container spacing={2}>
              {deliveries.map((d, i) => (
                <Grid item xs={12} md={6} xl={4} key={d.deliveryId ?? i}>
                  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", p: 2, height: "100%" }}>
                    <Stack direction="row" alignItems="center" spacing={1.25} mb={1.5}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "8px",
                          backgroundColor: BRAND,
                          color: "#fff",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        {i + 1}
                      </Box>
                      <SoftTypography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, flexGrow: 1 }}>
                        Lass {i + 1} av {deliveries.length}
                      </SoftTypography>
                      <Chip
                        size="small"
                        label={show(d.status_text)}
                        sx={{ backgroundColor: `${BRAND}14`, color: BRAND, fontWeight: 700 }}
                      />
                    </Stack>

                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Stat label="Volum" value={`${show(d.display_volume, "0")} m³`} />
                      </Grid>
                      <Grid item xs={6}>
                        <Stat label="Biltype" value={show(d.vehicle_type)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Stat label="Tidspunkt" value={show(d.time, "Avtales")} />
                      </Grid>
                      <Grid item xs={6}>
                        <Stat label="Pris (eks. mva.)" value={show(d.total_price_ex_vat)} />
                      </Grid>
                      <Grid item xs={12}>
                        <Stat label="Betong" value={show(d.concrete_type)} />
                      </Grid>
                      <Grid item xs={12}>
                        <Stat label="Tilsetning" value={additives(d)} />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
              {!deliveries.length && (
                <Grid item xs={12}>
                  <SoftTypography sx={{ fontSize: 13.5, color: MUTED }}>
                    Ingen leveranser registrert på denne ordren ennå.
                  </SoftTypography>
                </Grid>
              )}
            </Grid>
          </SoftBox>
        </Card>

        {/* ----------------------------- info cards ----------------------------- */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} xl={3}>
            <Card sx={CARD}>
              <SoftBox p={3}>
                <CardHead
                  icon={<PersonIcon sx={{ fontSize: 18 }} />}
                  title="Kundeinformasjon"
                  action={
                    isAdmin ? (
                      <SoftButton size="small" variant="text" color="info" onClick={() => setInfoOpen(true)}>
                        Rediger
                      </SoftButton>
                    ) : null
                  }
                />
                <InfoRow icon={<PersonIcon sx={{ fontSize: 16 }} />}>{show(data.fullname)}</InfoRow>
                <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />}>{show(data.phone)}</InfoRow>
                <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />}>{show(data.email)}</InfoRow>
                <InfoRow icon={<PlaceIcon sx={{ fontSize: 16 }} />}>{show(data.address)}</InfoRow>
              </SoftBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            <Card sx={CARD}>
              <SoftBox p={3}>
                <CardHead icon={<BusinessIcon sx={{ fontSize: 18 }} />} title="Leverandør" />
                <InfoRow icon={<BusinessIcon sx={{ fontSize: 16 }} />}>
                  {show(data.vendor_title)}
                </InfoRow>
                {isAdmin && (
                  <>
                    {/* Bytt produsent (Utvikling | BMAx s. 5 pkt. 2). Samme
                        change_vendor som den klassiske siden — den regner også
                        avstanden på nytt. */}
                    <Select
                      fullWidth
                      size="small"
                      displayEmpty
                      value=""
                      disabled={changingVendor}
                      onOpen={loadVendors}
                      onChange={(e) => applyVendor(e.target.value)}
                      sx={{ mb: 1.5, height: 36 }}
                      renderValue={() => (changingVendor ? "Bytter …" : "Bytt leverandør …")}
                    >
                      {!vendorOptions.length && <MenuItem disabled>Laster …</MenuItem>}
                      {vendorOptions.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.title}
                        </MenuItem>
                      ))}
                    </Select>
                    <SoftButton
                      fullWidth
                      size="small"
                      onClick={copyVendorLink}
                      sx={{
                        mb: 1.5,
                        borderRadius: "10px",
                        backgroundColor: `${BRAND}12`,
                        color: BRAND,
                        fontWeight: 700,
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Kopier leveranselenke
                    </SoftButton>
                    <SoftTypography
                      sx={{ fontSize: 11, color: MUTED, mb: 1.5, display: "block" }}
                    >
                      Send denne til produsenten — de bekrefter eller avslår uten å logge inn.
                    </SoftTypography>

                    {/* Melding til produsenten, vist på leveranselenken som
                        «Notat fra Betongsentrum». Kundens eget notat står for
                        seg — dette er hva VI vil at produsenten skal vite. */}
                    <SoftTypography sx={{ fontSize: 11, color: MUTED, display: "block", mb: 0.5 }}>
                      Notat til produsenten (valgfritt)
                    </SoftTypography>
                    <SoftInput
                      multiline
                      rows={2}
                      value={vendorNote}
                      placeholder="F.eks. kunden ønsker helst levering fra morgenen av."
                      onChange={(e) => setVendorNote(e.target.value.slice(0, 1000))}
                    />
                    <SoftButton
                      fullWidth
                      size="small"
                      onClick={handleSaveVendorNote}
                      disabled={savingVendorNote}
                      sx={{ mt: 1, mb: 1.5, borderRadius: "10px", backgroundColor: `${BRAND}12`, color: BRAND, fontWeight: 700 }}
                    >
                      {savingVendorNote ? "Lagrer…" : "Lagre notat"}
                    </SoftButton>
                  </>
                )}
                <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />}>
                  {show(data.vendor_email)}
                </InfoRow>
                <InfoRow icon={<PlaceIcon sx={{ fontSize: 16 }} />}>
                  {show(data.vendor_address)}
                </InfoRow>
                <InfoRow icon={<RouteIcon sx={{ fontSize: 16 }} />}>
                  {distanceLabel || "Avstand ikke beregnet"}
                </InfoRow>
              </SoftBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            <Card sx={CARD}>
              <SoftBox p={3}>
                <CardHead icon={<FolderIcon sx={{ fontSize: 18 }} />} title="Prosjektdetaljer" />
                <Stack spacing={1.5}>
                  <Stat label="Prosjekt" value={show(data.order_title)} />
                  <Stat label="Prosjektnr." value={show(data.step3?.projectNumber)} />
                  <Stat label="Opprettet" value={show(data.created_date?.date)} />
                  <Stat
                    label="Opprettet av"
                    value={`${show(data.created_by?.name)}${
                      data.created_by?.role ? ` (${data.created_by.role})` : ""
                    }`}
                  />
                  <Stat label="Merknad" value={show(data.customer_note, "Ingen")} />
                </Stack>
              </SoftBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            <Card sx={CARD}>
              <SoftBox p={3}>
                <CardHead icon={<DescriptionIcon sx={{ fontSize: 18 }} />} title="Dokumenter" />
                {Array.isArray(data.attached_images) && data.attached_images.length ? (
                  data.attached_images.map((f, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ backgroundColor: PAGE_BG, borderRadius: "10px", px: 1.5, py: 1, mb: 1 }}
                    >
                      <DescriptionIcon sx={{ fontSize: 17, color: MUTED }} />
                      <SoftTypography sx={{ fontSize: 13, flexGrow: 1, wordBreak: "break-all" }}>
                        {f?.originalName || `Vedlegg ${i + 1}`}
                      </SoftTypography>
                      {f?.url && (
                        <SoftButton
                          component="a"
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ minWidth: 0, color: BRAND }}
                        >
                          Åpne
                        </SoftButton>
                      )}
                    </Stack>
                  ))
                ) : (
                  <SoftTypography sx={{ fontSize: 13.5, color: MUTED }}>
                    Ingen dokumenter lastet opp.
                  </SoftTypography>
                )}

                {payUrl && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <SoftButton
                      component="a"
                      href={payUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{ borderColor: BRAND, color: BRAND, borderRadius: "10px" }}
                    >
                      <PaymentsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Åpne betalingsside
                    </SoftButton>
                  </>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>

        {/* --------------------- forespørsel til produsent ---------------------- */}
        {/* Spor B (FORESPORSEL-PLAN.md). Egen komponent fordi den samme
            oversikten trengs på den klassiske ordresiden også. */}
        {isAdmin && <VendorRequestPanel orderId={orderId} onOrderChanged={load} />}

        {/* ------------------------------ historikk ----------------------------- */}
        <Card sx={{ ...CARD, mb: 3, height: "auto" }}>
          <SoftBox p={3}>
            <CardHead icon={<EventIcon sx={{ fontSize: 18 }} />} title="Hendelseslogg" />
            <OrderEventTimeline orderId={orderId} onChange={() => load({ silent: true })} refreshKey={data} />
          </SoftBox>
        </Card>

        {/* Den klassiske ordresiden er fjernet — lenken dit er borte. */}
      </SoftBox>

      {/* Felles dialog for begge ordresidene. Viser e-posten som hører til den
          valgte statusen, lar admin rette den, og sender den sammen med
          statusendringen. */}
      <StatusChangeDialog
        open={statusOpen}
        orderId={orderId}
        currentStatus={statusKey}
        onClose={() => setStatusOpen(false)}
        onChanged={load}
      />
      <ManualEmailDialog
        open={manualOpen}
        orderId={orderId}
        currentStatus={statusKey}
        customerEmail={data?.email || data?.step3?.emailAddress || ""}
        vendorEmail={data?.vendor_email || ""}
        onClose={() => setManualOpen(false)}
        onSent={() => load({ silent: true })}
      />
      <StatusInfoDialog
        open={statusInfoOpen}
        currentStatus={statusKey}
        onClose={() => setStatusInfoOpen(false)}
      />
      <OrderInfoDialog
        open={infoOpen}
        orderId={orderId}
        step3={data?.step3}
        address={data?.step1_address}
        onClose={() => setInfoOpen(false)}
        onSaved={load}
      />

      {/* Admin: endre totalpris. Beløpet er inkl. mva. — backend fordeler det
          på lassene og regner ut mva. selv (update_new_total). */}
      <Dialog open={priceOpen} onClose={() => !priceSaving && setPriceOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Endre totalpris</DialogTitle>
        <DialogContent>
          <SoftTypography sx={{ fontSize: 13.5, color: MUTED, mb: 1.5 }}>
            Nåværende: <b>{data?.order_total}</b> inkl. mva. ({data?.order_subtotal} eks. mva.).
            Ny pris oppgis <b>inkludert mva.</b> — fordelingen per lass og mva.-beløpet beregnes
            automatisk.
          </SoftTypography>
          <SoftInput
            placeholder="F.eks. 20285,00"
            value={priceValue}
            onChange={(e) => setPriceValue(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setPriceOpen(false)} disabled={priceSaving}>
            Avbryt
          </SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSavePrice} disabled={priceSaving}>
            {priceSaving ? "Lagrer…" : "Lagre ny pris"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default OrderDetailsV2;
